import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface NumberFlowProps {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number; // ms
  className?: string;
  style?: CSSProperties;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

export function NumberFlow({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 800,
  className,
  style,
}: NumberFlowProps) {
  const [displayed, setDisplayed] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;

    startRef.current = null;

    function tick(now: number) {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(lerp(from, to, eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  const formatted = displayed.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className={cn("nf-root", className)} style={style} aria-live="polite" aria-atomic="true">
      {prefix && <span className="nf-prefix">{prefix}</span>}
      <span className="nf-value">{formatted}</span>
      {suffix && <span className="nf-suffix">{suffix}</span>}
    </span>
  );
}
